import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { groupBy } from 'lodash';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { BuiltLoadsGroup } from 'components/Kit/BuiltLoads/components/BuiltLoadsGroup';
import { PartitionedLoadBuildingSuggestions } from 'components/Kit/BuiltLoads/types';
import { isBuiltLoadBuildingSuggestion } from 'components/Kit/BuiltLoads/utils/isBuiltLoadBuildingSuggestion';
import { partitionLoadBuildingSuggestions } from 'components/Kit/BuiltLoads/utils/partitionLoadBuildingSuggestions';
import { HandledQuoteGroups } from 'components/Kit/HandledQuotes/components/HandledQuoteGroups';
import { HANDLED_GROUP_STATES } from 'components/Kit/HandledQuotes/constants';
import {
  HandledGroupState,
  PartitionedQuoteRequests,
  QuoteHandledState,
} from 'components/Kit/HandledQuotes/types';
import { partitionQuoteRequests } from 'components/Kit/HandledQuotes/utils/partitionQuoteRequests';
import { withSessionStatus } from 'components/Kit/HandledQuotes/utils/withSessionStatus';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { SidebarViewContext } from 'contexts/sidebarViewContext';
import { useAuth } from 'hooks/useAuth';
import { KitThreadCheckController } from 'hooks/useKitThreadCheck';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import {
  CloneJobStatus,
  LoadCloneRecommendation,
  LoadCloneRecommendationDisposition,
  updateLoadCloneRecommendation,
} from 'lib/api/cloneLoads';
import { resetQuoteRequests } from 'lib/api/resetQuoteRequests';
import { skipSuggestion } from 'lib/api/skipSuggestion';
import {
  areCarouselSuggestionListsEquivalent,
  isSharepointSuggestion,
  isSyntheticSharepointSuggestion,
} from 'pages/QuoteView/LoadBuilding/sharepoint/utils/sharepointSuggestions';
import {
  getBatchLTLSuggestions,
  isBatchLTLQuoteAvailable,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/BatchLTL/utils/getBatchLTLSuggestions';
import { Maybe, Undef } from 'types/UtilityTypes';
import SidebarView from 'types/enums/SidebarView';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';

import { AskKitThreadCheck } from './components/AskKitThreadCheck';
import {
  CompletedSuggestionStatus,
  CompletedSuggestionsToggle,
} from './components/CompletedSuggestionsToggle';
import { Header } from './components/Header';
import { KitThreadCheckMessage } from './components/KitThreadCheckMessage';
import {
  KitEmptyStateType,
  KitLoadEmptyStateType,
  MessageBubble,
} from './components/MessageBubble';
import { MessageExistingLoads } from './components/MessageExistingLoads';
import { ProcessingLatestEmailMessage } from './components/ProcessingLatestEmailMessage';
import { ScrollToTopPill } from './components/ScrollToTopPill';
import { Suggestion } from './components/Suggestion';
import { SuggestionList } from './components/SuggestionList';
import {
  KIT_PREVIEW_OPEN_DELAY_MS,
  LOAD_VIEW_PIPELINES,
  PIPELINE_DISPLAY_ORDER,
} from './constants';
import {
  KitSidebarView,
  buildKitFoundParts,
  hasKitSuggestionCounts,
} from './utils/buildKitFoundParts';
import {
  getKitLoadSelectionVariant,
  getKitQuoteSelectionVariant,
  joinKitFoundParts,
} from './utils/kitMessage';
import {
  countByPipeline,
  countPendingByPipeline,
  countPendingFromList,
  sumPipelineCounts,
} from './utils/kitSuggestionCounts';
import {
  isBlockedSpecialTransportSuggestion,
  isKitSuggestionApplyBlocked,
  isKitSuggestionFeatureEnabled,
  isLoadViewSuggestionUnsupportedForTms,
  sanitizeKitSuggestion,
} from './utils/kitSuggestionEligibility';
import {
  KitHandledQuoteBulkSelectionAction,
  KitHandledQuoteView,
  KitSuggestionSelectionSource,
  captureKitHandledQuoteBulkSelectionChanged,
  captureKitHandledQuoteReset,
  captureKitHandledQuoteSelectionChanged,
  captureKitHandledQuoteViewToggled,
  captureKitSuggestionSelected,
  getPosthogCapture,
} from './utils/posthogEvents';
import {
  type AppointmentDisplayTimezones,
  getActiveBatchLTLReplySummary,
} from './utils/suggestionFormatters';
import { suggestionSortComparator } from './utils/suggestionSortComparator';

export default function KitEmailSuggestions({
  suggestions: initRawSuggestions,
  currentEmailId,
  isProcessingLatestEmail = false,
  threadFreightTrackingIDs,
  // Carrier Quote tab: rows with AI-filled suggested pricing (workaround; not a SuggestionPipelines value).
  preloadedCarrierQuoteCount = 0,
  // Load view empty-state when nothing was found for this view.
  loadEmptyStateType = KitEmptyStateType.NoSuggestionsForLoad,
  onOpenAssociatedLoad,
  onOpenAllAssociatedLoads,
  onOpenBuiltLoad,
  // Preferred over TMS context when Kit mounts outside TMSProvider.
  tmsName: tmsNameProp,
  appointmentTimezones,
  cloneRecommendations = [],
  cloneRecommendationsError,
  onCloneRecommendationsChanged,
  onSelectCloneRecommendation,
  selectedCloneRecommendationId,
  cloneJobStatuses = {},
  blockedCloneRecommendationIds = [],
  onCloneRecommendationConflict,
  threadChecks = NO_THREAD_CHECKS,
}: {
  suggestions: GenericSuggestion[];
  currentEmailId?: number | null;
  // Quote view: the thread's latest email is still processing, so suggestions may still come in.
  isProcessingLatestEmail?: boolean;
  // When provided, Kit can link out to loads identified in the thread.
  threadFreightTrackingIDs?: Maybe<string[]>;
  // Quote view: count of carrier quotes with `suggestedQuote` preloaded for the Carrier Quote tab.
  preloadedCarrierQuoteCount?: number;
  loadEmptyStateType?: KitLoadEmptyStateType;
  onOpenAssociatedLoad?: (freightTrackingID: string) => void;
  onOpenAllAssociatedLoads?: () => void;
  // Quote view: opens the load a built load building card became, by freight tracking ID or
  // external TMS ID.
  onOpenBuiltLoad?: (loadIdentifier: string) => void;
  tmsName?: Maybe<string>;
  // Load stop timezones for Appointment pill titles (never browser-local).
  appointmentTimezones?: AppointmentDisplayTimezones;
  cloneRecommendations?: LoadCloneRecommendation[];
  cloneRecommendationsError?: boolean;
  onCloneRecommendationsChanged?: (
    recommendation?: LoadCloneRecommendation
  ) => void;
  onSelectCloneRecommendation?: (
    recommendation: LoadCloneRecommendation
  ) => void;
  selectedCloneRecommendationId?: Maybe<number>;
  cloneJobStatuses?: Partial<Record<number, CloneJobStatus>>;
  blockedCloneRecommendationIds?: number[];
  onCloneRecommendationConflict?: (
    recommendation: LoadCloneRecommendation
  ) => Promise<boolean>;
  // Quote view: one per kind the rep can ask Kit to check the thread for.
  threadChecks?: KitThreadCheckController[];
}) {
  const { user } = useAuth();
  const posthog = usePostHog();
  const { toast } = useToast();
  const {
    setCurrentState,
    currentState: {
      curSuggestionList,
      clickedSuggestion,
      batchSelectedSuggestionIds,
      batchLTLReplySummary,
      threadId,
      shouldKitAutoSelect,
    },
  } = useContext(SidebarStateContext);
  const threadGenerationRef = useRef(0);
  const { currentView, setCurrentView } = useContext(SidebarViewContext);

  const {
    serviceFeaturesEnabled: {
      isCarrierInfoSuggestionsEnabled,
      isAppointmentSuggestionsEnabled,
      isLoadBuildingEnabled,
      isBatchQuoteEnabled,
      isLTLQuickQuoteEnabled,
      isFTLQuickQuoteEnabled,
      isLoadCloningEnabled,
      isQuoteRequestResetEnabled,
    },
    carrierQuoteConfig,
    ltlTMSIntegrations,
  } = useServiceFeatures();
  const isBatchLTLEnabled = isBatchLTLQuoteAvailable({
    isLTLQuickQuoteEnabled,
    ltlTMSIntegrations,
  });
  const isQuoteView = currentView === SidebarView.Quote;
  const visibleCloneRecommendations =
    isQuoteView && isLoadCloningEnabled
      ? cloneRecommendations.filter(
          (recommendation) =>
            recommendation.disposition ===
              LoadCloneRecommendationDisposition.Pending ||
            recommendation.disposition ===
              LoadCloneRecommendationDisposition.Started
        )
      : [];
  const pendingCloneCount = visibleCloneRecommendations.filter(
    (recommendation) =>
      cloneJobStatuses[recommendation.id] !== CloneJobStatus.Succeeded
  ).length;
  const { tmsName: tmsNameFromContext } = useTMSContext();
  const tmsName = tmsNameProp || tmsNameFromContext || null;

  const carrierQuoteTabLabel =
    carrierQuoteConfig?.customName !== ''
      ? carrierQuoteConfig?.customName
      : 'Carrier Quote';

  const [optionalSuggestions, setOptionalSuggestions] = useState<
    GenericSuggestion[]
  >([]);
  const [mutatingCloneRecommendationIds, setMutatingCloneRecommendationIds] =
    useState<number[]>([]);
  const cloneRecommendationsRef = useRef(cloneRecommendations);
  cloneRecommendationsRef.current = cloneRecommendations;

  const kitSectionRef = useRef<HTMLDivElement>(null);
  const posthogCapture = getPosthogCapture(posthog);
  // Dedupe selection events
  const [lastCapturedSelectionId, setLastCapturedSelectionId] =
    useState<Maybe<number>>(null);

  const captureSelection = useCallback(
    (
      suggestion: GenericSuggestion,
      selectionSource: KitSuggestionSelectionSource,
      batchCount?: number
    ): void => {
      captureKitSuggestionSelected({
        capture: posthogCapture,
        suggestion,
        serviceID: user?.service_id,
        selectionSource,
        batchCount,
      });
      setLastCapturedSelectionId(suggestion.id);
    },
    [posthogCapture, user?.service_id]
  );

  const [expandedPipeline, setExpandedPipeline] =
    useState<Maybe<SuggestionPipelines>>(null);
  const [showCompletedSuggestions, setShowCompletedSuggestions] =
    useState(false);
  const [selectedHandledQuoteIds, setSelectedHandledQuoteIds] = useState<
    Set<number>
  >(new Set());

  const [visibleCountByPipeline, setVisibleCountByPipeline] = useState<
    Partial<Record<SuggestionPipelines, number>>
  >({});

  useEffect(() => {
    const kitFeatureFlags = {
      isCarrierInfoSuggestionsEnabled,
      isAppointmentSuggestionsEnabled,
      isLoadBuildingEnabled,
      isLTLQuickQuoteEnabled,
      isFTLQuickQuoteEnabled,
    };
    const fromApiSuggestions = initRawSuggestions
      .sort(suggestionSortComparator)
      .filter((suggestion) =>
        isKitSuggestionFeatureEnabled(suggestion, kitFeatureFlags)
      )
      .map(sanitizeKitSuggestion);

    // Email/API suggestions only - SharePoint rows (persisted or fail-open synthetic)
    // are merged in via `useSharepointLoadBuildingSync`. Do not replace
    // `curSuggestionList` with API-only data or SharePoint suggestions disappear after
    // create-load when suggestions refetch.
    setCurrentState((prevState) => {
      const sharepointSuggestions = prevState.curSuggestionList.filter(
        isSharepointSuggestion
      );

      const suggestionsAcceptedThisSession = new Map(
        prevState.curSuggestionList
          .filter(
            (s) =>
              !isSharepointSuggestion(s) &&
              s.status === SuggestionStatus.Accepted
          )
          .map((s) => [s.id, s])
      );
      // Only overlay session Accepted onto ids still in the latest API payload.
      // Replied, quoted and skipped quote requests render in HandledQuoteGroups instead, and built
      // load building cards in BuiltLoadsGroup.
      const emailAndApiSuggestions = fromApiSuggestions
        .filter(
          (s) =>
            !isSharepointSuggestion(s) &&
            s.status !== SuggestionStatus.Accepted &&
            !isHandledQuoteRequest(s) &&
            !shouldMoveToBuiltGroup(s, prevState.clickedSuggestion?.id)
        )
        .map((s) => {
          if (!suggestionsAcceptedThisSession.has(s.id)) {
            return s;
          }
          return { ...s, status: SuggestionStatus.Accepted };
        });
      const merged = [...sharepointSuggestions, ...emailAndApiSuggestions];
      // Bidding-portal scrapes recreate suggestion objects for the same load.
      // Skip context updates when identity is unchanged to avoid remount loops.
      if (
        areCarouselSuggestionListsEquivalent(
          prevState.curSuggestionList,
          merged
        ) &&
        !prevState.curSuggestionList.some(
          (s, i) => s.status !== merged[i]?.status
        )
      ) {
        return prevState;
      }
      return {
        ...prevState,
        curSuggestionList: merged,
      };
    });
  }, [
    initRawSuggestions,
    isAppointmentSuggestionsEnabled,
    isCarrierInfoSuggestionsEnabled,
    isLoadBuildingEnabled,
    isLTLQuickQuoteEnabled,
    isFTLQuickQuoteEnabled,
    isBatchQuoteEnabled,
    setCurrentState,
  ]);

  useEffect(() => {
    setOptionalSuggestions((prev) => {
      if (prev === curSuggestionList) {
        return prev;
      }
      if (!areCarouselSuggestionListsEquivalent(prev, curSuggestionList)) {
        return curSuggestionList;
      }
      const statusChanged = prev.some(
        (s, i) => s.status !== curSuggestionList[i]?.status
      );
      return statusChanged ? curSuggestionList : prev;
    });
  }, [curSuggestionList]);

  // Auto-select all pending Quick Quotes when the pending id-set changes and batch is enabled.
  // User removes from Batch do not change this pending set, so they won't re-select.
  const pendingQqIdsKeyRef = useRef('');
  useEffect(() => {
    if (!shouldKitAutoSelect) {
      // Clear so switching back to Quick Quote can auto-select all pending lanes again
      pendingQqIdsKeyRef.current = '';
      return;
    }

    const pendingQq = curSuggestionList.filter(
      (s) =>
        s.pipeline === SuggestionPipelines.QuickQuote &&
        isQuoteRequestActionable(s) &&
        !isBlockedSpecialTransportSuggestion(s)
    );
    const pendingIds = pendingQq.map((s) => s.id);
    const batchLTLPendingIds = getBatchLTLSuggestions(
      pendingQq,
      currentEmailId
    ).map((suggestion) => suggestion.id);

    // Batch LTL selects the email's LTL shipments even when FTL Batch Quote is off.
    const isBatchLTLSelectionEnabled =
      isBatchLTLEnabled && batchLTLPendingIds.length > 0;
    const selectionIds = isBatchLTLSelectionEnabled
      ? batchLTLPendingIds
      : pendingIds;
    const isBatchSelectionEnabled =
      isBatchQuoteEnabled || isBatchLTLSelectionEnabled;
    const key = [...selectionIds].sort((a, b) => a - b).join(',');

    if (!isBatchSelectionEnabled || selectionIds.length <= 1) {
      if (batchSelectedSuggestionIds.length > 0) {
        setCurrentState((prev) => ({
          ...prev,
          batchSelectedSuggestionIds: [],
        }));
      }
      pendingQqIdsKeyRef.current = key;
      return;
    }

    // Never steal clickedSuggestion from another pipeline (e.g. Load Building).
    if (key !== pendingQqIdsKeyRef.current) {
      pendingQqIdsKeyRef.current = key;

      const preserveNonQqSelection =
        clickedSuggestion != null &&
        clickedSuggestion.pipeline !== SuggestionPipelines.QuickQuote;
      const nextClicked = preserveNonQqSelection
        ? clickedSuggestion
        : (pendingQq.find(({ id }) => selectionIds.includes(id)) ??
          clickedSuggestion);

      if (
        !preserveNonQqSelection &&
        nextClicked &&
        nextClicked.id !== clickedSuggestion?.id
      ) {
        captureSelection(nextClicked, KitSuggestionSelectionSource.AutoSelect);
      }

      setCurrentState((prev) => ({
        ...prev,
        batchSelectedSuggestionIds: selectionIds,
        clickedSuggestion: nextClicked,
        shouldKitAutoSelect: true,
      }));
    }
  }, [
    curSuggestionList,
    currentEmailId,
    isBatchQuoteEnabled,
    isBatchLTLEnabled,
    batchSelectedSuggestionIds.length,
    clickedSuggestion,
    shouldKitAutoSelect,
    setCurrentState,
    captureSelection,
  ]);

  // Capture untagged clickedSuggestion changes (e.g. initial auto-select / tab switch)
  useEffect(() => {
    if (!clickedSuggestion?.pipeline) {
      if (lastCapturedSelectionId != null) {
        setLastCapturedSelectionId(null);
      }
      return;
    }
    if (lastCapturedSelectionId === clickedSuggestion.id) {
      return;
    }

    const selectionSource =
      lastCapturedSelectionId == null
        ? KitSuggestionSelectionSource.AutoSelect
        : KitSuggestionSelectionSource.AutoAdvance;

    captureKitSuggestionSelected({
      capture: posthogCapture,
      suggestion: clickedSuggestion,
      serviceID: user?.service_id,
      selectionSource,
    });
    setLastCapturedSelectionId(clickedSuggestion.id);
  }, [
    clickedSuggestion,
    lastCapturedSelectionId,
    posthogCapture,
    user?.service_id,
  ]);

  // Expand the category that contains the selected suggestion.
  // - On Load view, keep the last category open after completion so the Done pill stays visible
  // Collapse (expand null) when selection clears and auto-select is disabled on Quote view.
  useEffect(() => {
    if (selectedCloneRecommendationId != null) {
      setExpandedPipeline(SuggestionPipelines.LoadBuilding);
      return;
    }
    if (clickedSuggestion?.pipeline != null) {
      setExpandedPipeline(clickedSuggestion.pipeline);
      return;
    }

    if (isQuoteView && !shouldKitAutoSelect) {
      setExpandedPipeline(null);
    }
  }, [
    selectedCloneRecommendationId,
    clickedSuggestion?.id,
    clickedSuggestion?.pipeline,
    isQuoteView,
    shouldKitAutoSelect,
  ]);

  // Load view: open the first pending pipeline by default (Carrier Info -> Appointment -> Check Call) even when
  // nothing is auto-selected. Only re-run when that "first pending" target changes so
  // a manual collapse is not immediately overridden.
  const loadViewDefaultExpandRef = useRef<Maybe<SuggestionPipelines>>(null);
  useEffect(() => {
    if (isQuoteView) {
      loadViewDefaultExpandRef.current = null;
      return;
    }

    if (clickedSuggestion?.pipeline != null) {
      loadViewDefaultExpandRef.current = clickedSuggestion.pipeline;
      return;
    }

    const firstPendingPipeline = LOAD_VIEW_PIPELINES.find((pipeline) =>
      optionalSuggestions.some(
        (s) => s.pipeline === pipeline && s.status !== SuggestionStatus.Accepted
      )
    );

    if (!firstPendingPipeline) {
      loadViewDefaultExpandRef.current = null;
      return;
    }

    if (loadViewDefaultExpandRef.current !== firstPendingPipeline) {
      loadViewDefaultExpandRef.current = firstPendingPipeline;
      setExpandedPipeline(firstPendingPipeline);
    }
  }, [isQuoteView, optionalSuggestions, clickedSuggestion?.pipeline]);

  // Auto-collapse only when a pipeline *becomes* fully completed (not when user re-opens it).
  const pendingCountByPipelineRef = useRef<
    Partial<Record<SuggestionPipelines, number>>
  >({});
  useEffect(() => {
    const nextPending = countPendingFromList(optionalSuggestions);
    nextPending[SuggestionPipelines.LoadBuilding] =
      (nextPending[SuggestionPipelines.LoadBuilding] ?? 0) + pendingCloneCount;

    const prevPending = pendingCountByPipelineRef.current;
    for (const pipeline of Object.keys(prevPending) as SuggestionPipelines[]) {
      const wasPending = (prevPending[pipeline] ?? 0) > 0;
      const nowPending = (nextPending[pipeline] ?? 0) > 0;
      if (wasPending && !nowPending && expandedPipeline === pipeline) {
        setExpandedPipeline(null);
      }
    }

    pendingCountByPipelineRef.current = nextPending;
  }, [optionalSuggestions, expandedPipeline, pendingCloneCount]);

  const handleGoToSuggestion = useCallback(
    async (params: {
      suggestionID?: number;
      suggestionPipeline?: SuggestionPipelines;
    }) => {
      const { suggestionID, suggestionPipeline } = params;

      let resolved: Undef<GenericSuggestion>;
      setCurrentState((prevState) => {
        // User cleared selection by switching to a tab with no pending
        // suggestion — do not let forms / goToSuggestion re-select.
        if (!prevState.shouldKitAutoSelect) {
          return prevState;
        }

        let target: Undef<GenericSuggestion>;
        if (suggestionID != null) {
          target = prevState.curSuggestionList.find(
            (s) => s.id === suggestionID
          );
        } else if (suggestionPipeline != null) {
          target = prevState.curSuggestionList.find(
            (s) =>
              s.pipeline === suggestionPipeline &&
              s.status !== SuggestionStatus.Accepted &&
              !isLoadViewSuggestionUnsupportedForTms(s, tmsName)
          );
        }

        if (
          !target ||
          prevState.clickedSuggestion?.id === target.id ||
          isLoadViewSuggestionUnsupportedForTms(target, tmsName)
        ) {
          return prevState;
        }

        resolved = target;
        return {
          ...prevState,
          clickedSuggestion: target,
          batchSelectedSuggestionIds: [],
          shouldKitAutoSelect: true,
        };
      });

      if (resolved) {
        captureSelection(resolved, KitSuggestionSelectionSource.GoToSuggestion);
      }
    },
    [tmsName, setCurrentState, captureSelection]
  );

  // use ref wrapper so handler updates don't re-trigger setState in loop
  const handleGoToSuggestionRef = useRef(handleGoToSuggestion);
  handleGoToSuggestionRef.current = handleGoToSuggestion;

  useEffect(() => {
    const goToSuggestion = async (params: {
      suggestionID?: number;
      suggestionPipeline?: SuggestionPipelines;
    }) => handleGoToSuggestionRef.current(params);

    setCurrentState((prevState) => ({
      ...prevState,
      goToSuggestion,
    }));
    // Re-register on thread change
  }, [setCurrentState, threadId]);

  const handleApplySuggestion = (suggestion: GenericSuggestion): void => {
    if (isKitSuggestionApplyBlocked(suggestion, tmsName)) {
      return;
    }
    captureSelection(suggestion, KitSuggestionSelectionSource.UserClick);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: suggestion,
      batchSelectedSuggestionIds: [],
      shouldKitAutoSelect: true,
    }));
  };

  const handleApplyBatch = (suggestions: GenericSuggestion[]): void => {
    const appliable = suggestions.filter(
      (s) => !isKitSuggestionApplyBlocked(s, tmsName)
    );
    if (appliable.length === 0) {
      return;
    }
    captureSelection(
      appliable[0],
      KitSuggestionSelectionSource.BatchApplyAll,
      appliable.length
    );
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: appliable[0],
      batchSelectedSuggestionIds: appliable.map((s) => s.id),
      shouldKitAutoSelect: true,
    }));
  };

  const removeSuggestionFromList = (suggestion: GenericSuggestion) => {
    setCurrentState((prev) => ({
      ...prev,
      clickedSuggestion:
        prev.clickedSuggestion?.id === suggestion.id
          ? null
          : prev.clickedSuggestion,
      batchSelectedSuggestionIds: (
        prev.batchSelectedSuggestionIds ?? []
      ).filter((id) => id !== suggestion.id),
      // Must update `curSuggestionList` (not only local state): SharePoint sync
      // rebuilds from context and would resurrect skipped suggestions otherwise.
      curSuggestionList: prev.curSuggestionList.filter(
        (s) => s.id !== suggestion.id
      ),
    }));
  };

  const markQuickQuoteDismissed = (suggestionId: number) => {
    setCurrentState((prev) => ({
      ...prev,
      curSuggestionList: prev.curSuggestionList.map((suggestion) =>
        suggestion.id === suggestionId
          ? { ...suggestion, status: SuggestionStatus.Rejected }
          : suggestion
      ),
      clickedSuggestion:
        prev.clickedSuggestion?.id === suggestionId
          ? null
          : prev.clickedSuggestion,
      batchSelectedSuggestionIds: (
        prev.batchSelectedSuggestionIds ?? []
      ).filter((id) => id !== suggestionId),
    }));
  };

  const handleDismissSuggestion = async (
    suggestion: GenericSuggestion
  ): Promise<void> => {
    // Fail-open SharePoint rows use negative client-side ids; no DB row to skip.
    // Persisted SharePoint rows (positive ids) fall through to `skipSuggestion`.
    if (isSyntheticSharepointSuggestion(suggestion)) {
      removeSuggestionFromList(suggestion);
      toast({
        description: 'Suggestion dismissed.',
        variant: 'default',
      });
      return;
    }

    const res = await skipSuggestion(suggestion.id);

    if (res.isOk()) {
      if (suggestion.pipeline === SuggestionPipelines.QuickQuote) {
        // Keep dismissed Quick Quotes in context so Handled Quotes can show them as Skipped
        markQuickQuoteDismissed(suggestion.id);
      } else {
        // Other suggestion types have no handled section, so remove them entirely
        removeSuggestionFromList(suggestion);
      }
      toast({
        description: 'Suggestion dismissed.',
        variant: 'default',
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }
  };

  // Quote view: API quote requests split into active lanes and handled groups. Derived from
  // props + context (no state), so a lane quoted this session moves to Quoted right away.
  const handledQuoteRequests = useMemo(
    () =>
      isQuoteView
        ? partitionQuoteRequests(
            withSessionStatus(
              initRawSuggestions.filter((s) =>
                isKitSuggestionFeatureEnabled(s, {
                  isCarrierInfoSuggestionsEnabled,
                  isAppointmentSuggestionsEnabled,
                  isLoadBuildingEnabled,
                  isLTLQuickQuoteEnabled,
                  isFTLQuickQuoteEnabled,
                })
              ),
              curSuggestionList
            )
          )
        : null,
    [
      isQuoteView,
      initRawSuggestions,
      curSuggestionList,
      isCarrierInfoSuggestionsEnabled,
      isAppointmentSuggestionsEnabled,
      isLoadBuildingEnabled,
      isLTLQuickQuoteEnabled,
      isFTLQuickQuoteEnabled,
    ]
  );
  const handledSuggestionCount =
    handledQuoteRequests?.handled != null
      ? HANDLED_GROUP_STATES.reduce(
          (count, state) =>
            count +
            handledQuoteRequests.handled[state].reduce(
              (batchCount, batch) => batchCount + batch.length,
              0
            ),
          0
        )
      : 0;
  const handledSuggestionStatuses =
    handledQuoteRequests?.handled != null
      ? HANDLED_GROUP_STATES.map((state) => ({
          state,
          count: handledQuoteRequests.handled[state].reduce(
            (total, batch) => total + batch.length,
            0
          ),
        })).filter(({ count }) => count > 0)
      : [];

  // Quote view: the thread's load building cards split into open and built, in the API's
  // first-seen order. Derived from props, so a refetch that marks a card built moves it without an
  // effect.
  const loadBuildingSuggestions = useMemo(
    () =>
      isQuoteView && isLoadBuildingEnabled
        ? partitionLoadBuildingSuggestions(initRawSuggestions)
        : null,
    [isQuoteView, isLoadBuildingEnabled, initRawSuggestions]
  );
  // A built card that stays in the Kit list because the rep had it open isn't repeated in the
  // Built group.
  const builtLoadBuildingSuggestions = useMemo(
    () =>
      (loadBuildingSuggestions?.built ?? []).filter(
        (built) => !curSuggestionList.some((s) => s.id === built.id)
      ),
    [loadBuildingSuggestions, curSuggestionList]
  );
  const builtLoadCount = onOpenBuiltLoad
    ? builtLoadBuildingSuggestions.length
    : 0;
  const hasCompletedSuggestions =
    handledSuggestionCount > 0 || builtLoadCount > 0;
  const completedSuggestionStatuses: CompletedSuggestionStatus[] = [
    ...handledSuggestionStatuses.map(({ state, count }) => ({
      key: state,
      count,
      colorClass: getHandledStatusIndicatorClass(state),
    })),
    ...(builtLoadCount > 0
      ? [{ key: 'built', count: builtLoadCount, colorClass: 'text-info-700' }]
      : []),
  ];
  useEffect(() => {
    threadGenerationRef.current += 1;
    setShowCompletedSuggestions(false);
    setSelectedHandledQuoteIds(new Set());
  }, [threadId]);

  const toggleHandledQuoteSelection = useCallback(
    (laneId: number) => {
      const selected = !selectedHandledQuoteIds.has(laneId);
      captureKitHandledQuoteSelectionChanged({
        capture: posthogCapture,
        laneId,
        selected,
        selectionCount: selectedHandledQuoteIds.size + (selected ? 1 : -1),
      });
      setSelectedHandledQuoteIds((current) => {
        const next = new Set(current);
        if (!selected) {
          next.delete(laneId);
        } else {
          next.add(laneId);
        }
        return next;
      });
    },
    [posthogCapture, selectedHandledQuoteIds]
  );

  const selectHandledQuoteLanes = useCallback(
    (laneIds: number[]) => {
      const shouldClear = laneIds.every((laneId) =>
        selectedHandledQuoteIds.has(laneId)
      );
      captureKitHandledQuoteBulkSelectionChanged({
        capture: posthogCapture,
        action: shouldClear
          ? KitHandledQuoteBulkSelectionAction.Clear
          : KitHandledQuoteBulkSelectionAction.Select,
        laneCount: laneIds.length,
      });
      setSelectedHandledQuoteIds((current) => {
        const next = new Set(current);
        for (const laneId of laneIds) {
          if (shouldClear) {
            next.delete(laneId);
          } else {
            next.add(laneId);
          }
        }
        return next;
      });
    },
    [posthogCapture, selectedHandledQuoteIds]
  );

  const resetSelectedHandledQuotes = useCallback(() => {
    const quoteRequestIds = [...selectedHandledQuoteIds];
    if (quoteRequestIds.length === 0) {
      return;
    }

    const quoteRequestIdSet = new Set(quoteRequestIds);
    const resetThreadGeneration = threadGenerationRef.current;
    const previousSuggestionList = curSuggestionList;
    const previousClickedSuggestion = clickedSuggestion;
    const previousBatchSelectedSuggestionIds = batchSelectedSuggestionIds;
    const previousShouldKitAutoSelect = shouldKitAutoSelect;
    const previousSelectedHandledQuoteIds = selectedHandledQuoteIds;
    const previousShowCompletedSuggestions = showCompletedSuggestions;
    const resetSuggestions = initRawSuggestions
      .filter(
        (suggestion): suggestion is SuggestedQuoteChange =>
          suggestion.pipeline === SuggestionPipelines.QuickQuote &&
          quoteRequestIdSet.has(suggestion.id)
      )
      .map(
        ({
          applied: _applied,
          handledState: _handledState,
          handledReason: _handledReason,
          handledAt: _handledAt,
          handledByName: _handledByName,
          quotedAmount: _quotedAmount,
          ...suggestion
        }) => ({
          ...suggestion,
          status: SuggestionStatus.Pending,
        })
      );

    const resetIds = resetSuggestions.map(({ id }) => id);
    setCurrentState((prevState) => ({
      ...prevState,
      curSuggestionList: [
        ...prevState.curSuggestionList.filter(
          (suggestion) => !quoteRequestIdSet.has(suggestion.id)
        ),
        ...resetSuggestions,
      ],
      clickedSuggestion: resetSuggestions[0] ?? prevState.clickedSuggestion,
      batchSelectedSuggestionIds: resetIds.length > 1 ? resetIds : [],
      shouldKitAutoSelect: false,
    }));
    setSelectedHandledQuoteIds(new Set());
    setShowCompletedSuggestions(false);
    captureKitHandledQuoteReset({
      capture: posthogCapture,
      quoteCount: quoteRequestIds.length,
    });

    void resetQuoteRequests(quoteRequestIds).then((result: any) => {
      if (result.isErr()) {
        if (threadGenerationRef.current === resetThreadGeneration) {
          setCurrentState((prevState) => ({
            ...prevState,
            curSuggestionList: [
              ...prevState.curSuggestionList.filter(
                (suggestion) => !quoteRequestIdSet.has(suggestion.id)
              ),
              ...previousSuggestionList.filter((suggestion) =>
                quoteRequestIdSet.has(suggestion.id)
              ),
            ],
            clickedSuggestion: previousClickedSuggestion,
            batchSelectedSuggestionIds: previousBatchSelectedSuggestionIds,
            shouldKitAutoSelect: previousShouldKitAutoSelect,
          }));
          setSelectedHandledQuoteIds(previousSelectedHandledQuoteIds);
          setShowCompletedSuggestions(previousShowCompletedSuggestions);
        }
        toast({ description: result.error.message, variant: 'destructive' });
        return;
      }
      toast({
        description: `${quoteRequestIds.length} lane${
          quoteRequestIds.length === 1 ? '' : 's'
        } ready for requoting.`,
      });
    });
  }, [
    initRawSuggestions,
    batchSelectedSuggestionIds,
    clickedSuggestion,
    curSuggestionList,
    posthogCapture,
    selectedHandledQuoteIds,
    setCurrentState,
    shouldKitAutoSelect,
    showCompletedSuggestions,
    toast,
  ]);

  // Session-completed Quick Quote lanes show under Quoted, not as Done pills.
  const groupedSuggestions = groupBy(
    optionalSuggestions.filter((s) => !isHandledQuoteRequest(s)),
    (s) => s.pipeline
  );
  const countsByPipeline = countByPipeline(groupedSuggestions);
  const pendingByPipeline = countPendingByPipeline(groupedSuggestions);
  countsByPipeline[SuggestionPipelines.LoadBuilding] =
    (countsByPipeline[SuggestionPipelines.LoadBuilding] ?? 0) +
    visibleCloneRecommendations.length;
  pendingByPipeline[SuggestionPipelines.LoadBuilding] =
    (pendingByPipeline[SuggestionPipelines.LoadBuilding] ?? 0) +
    pendingCloneCount;

  const dismissCloneRecommendation = async (
    recommendation: LoadCloneRecommendation
  ) => {
    if (
      blockedCloneRecommendationIds.includes(recommendation.id) ||
      mutatingCloneRecommendationIds.includes(recommendation.id)
    )
      return;
    setMutatingCloneRecommendationIds((current) => [
      ...current,
      recommendation.id,
    ]);
    const result = await updateLoadCloneRecommendation(recommendation.id, {
      version: recommendation.version,
      disposition: LoadCloneRecommendationDisposition.Dismissed,
    });
    setMutatingCloneRecommendationIds((current) =>
      current.filter((id) => id !== recommendation.id)
    );
    const current = cloneRecommendationsRef.current.find(
      (item) => item.id === recommendation.id
    );
    if (
      !current ||
      current.threadId !== recommendation.threadId ||
      current.version !== recommendation.version
    )
      return;
    if (result.isErr()) {
      toast({ description: result.error.message, variant: 'destructive' });
      if (result.error.status === 409) {
        await onCloneRecommendationConflict?.(recommendation);
        return;
      }
    } else {
      posthog?.capture('load_clone_recommendation_dismissed', {
        recommendation_id: recommendation.id,
      });
      onCloneRecommendationsChanged?.(result.value);
    }
  };

  const activeBatchLTLReplySummary = getActiveBatchLTLReplySummary(
    batchLTLReplySummary,
    curSuggestionList
  );
  const batchLTLShipmentCount = isBatchLTLEnabled
    ? getBatchLTLSuggestions(curSuggestionList, currentEmailId).length
    : 0;

  const kitMessageView = isQuoteView
    ? KitSidebarView.Quote
    : KitSidebarView.Load;
  const foundParts = buildKitFoundParts({
    view: kitMessageView,
    countsByPipeline: {
      ...countsByPipeline,
      [SuggestionPipelines.LoadBuilding]:
        (countsByPipeline[SuggestionPipelines.LoadBuilding] ?? 0) -
        visibleCloneRecommendations.length,
    },
    preloadedCarrierQuoteCount,
  });
  if (isQuoteView && visibleCloneRecommendations.length > 0) {
    const count = visibleCloneRecommendations.length;
    foundParts.push(`${count} cloning suggestion${count === 1 ? '' : 's'}`);
  }
  const hasKitSuggestions = hasKitSuggestionCounts(
    countsByPipeline,
    kitMessageView
  );

  const quoteSelectionVariant = getKitQuoteSelectionVariant({
    pendingQuickQuoteCount:
      pendingByPipeline[SuggestionPipelines.QuickQuote] ?? 0,
    pendingLoadBuildingCount:
      pendingByPipeline[SuggestionPipelines.LoadBuilding] ?? 0,
    isBatchQuoteEnabled,
    batchSelectedCount: batchSelectedSuggestionIds.length,
    batchLTLShipmentCount,
    hasBatchLTLReply: Boolean(activeBatchLTLReplySummary),
  });
  const loadSelectionVariant = getKitLoadSelectionVariant(
    sumPipelineCounts(pendingByPipeline, LOAD_VIEW_PIPELINES)
  );
  const selectionVariant = isQuoteView
    ? quoteSelectionVariant
    : loadSelectionVariant;

  const threadLoadCount = threadFreightTrackingIDs?.length ?? 0;
  const hasThreadLoads = threadLoadCount > 0;
  const hasFoundParts = foundParts.length > 0;

  let emptyStateType: Maybe<KitEmptyStateType> = null;
  if (!hasFoundParts) {
    emptyStateType = isQuoteView
      ? getQuoteViewEmptyStateType(
          handledQuoteRequests,
          loadBuildingSuggestions
        )
      : loadEmptyStateType;
  }

  const pipelinesToShow = isQuoteView
    ? PIPELINE_DISPLAY_ORDER.filter((p) => !LOAD_VIEW_PIPELINES.includes(p))
    : PIPELINE_DISPLAY_ORDER;
  const pendingSuggestionCount = sumPipelineCounts(
    pendingByPipeline,
    pipelinesToShow
  );

  const openLoadsView = () => setCurrentView(SidebarView.Loads);

  const associatedLoadIds = (threadFreightTrackingIDs ?? []).filter(
    (id) => id.trim() !== ''
  );

  const associatedLoadsReopen =
    emptyStateType === KitEmptyStateType.AssociatedLoadsClosed &&
    associatedLoadIds.length > 0 &&
    onOpenAssociatedLoad &&
    onOpenAllAssociatedLoads
      ? {
          freightTrackingIDs: associatedLoadIds,
          onOpenLoad: onOpenAssociatedLoad,
          onOpenAll: onOpenAllAssociatedLoads,
        }
      : null;

  const hideSuggestionList =
    emptyStateType === KitEmptyStateType.NoAssociatedLoads ||
    emptyStateType === KitEmptyStateType.AssociatedLoadsClosed;

  // An empty Kit isn't "no suggestions" until the latest email is processed.
  const showProcessingMessage =
    isQuoteView && isProcessingLatestEmail && emptyStateType != null;
  const visibleThreadChecks = isQuoteView ? threadChecks : NO_THREAD_CHECKS;
  const isThreadCheckRunning = visibleThreadChecks.some(
    (check) => check.isRunning
  );

  return (
    <TooltipProvider
      delayDuration={0}
      skipDelayDuration={KIT_PREVIEW_OPEN_DELAY_MS}
    >
      <div
        ref={kitSectionRef}
        className='group w-[calc(100%-32px)] mt-0 mb-4 mx-auto'
      >
        <ScrollToTopPill kitSectionRef={kitSectionRef} />
        <Flex direction='col' gap='md'>
          <Header
            isProcessing={showProcessingMessage || isThreadCheckRunning}
            bubble={
              showProcessingMessage ? (
                <ProcessingLatestEmailMessage />
              ) : (
                <MessageBubble
                  foundParts={foundParts}
                  hasKitSuggestions={hasKitSuggestions}
                  carrierQuoteTabLabel={carrierQuoteTabLabel ?? ''}
                  autoSelectsSuggestion={
                    isQuoteView && clickedSuggestion != null
                  }
                  showingHandledQuotes={isQuoteView && showCompletedSuggestions}
                  handledQuoteCount={handledSuggestionCount}
                  manualSelectionMessage={
                    isQuoteView
                      ? `Hey! I found ${joinKitFoundParts(foundParts)} in this email. Select a suggestion to review it.`
                      : undefined
                  }
                  selectionVariant={selectionVariant}
                  batchLTLShipmentCount={batchLTLShipmentCount}
                  batchLTLReplySummary={activeBatchLTLReplySummary}
                  emptyStateType={emptyStateType}
                  associatedLoadsReopen={associatedLoadsReopen}
                />
              )
            }
            existingLoads={
              isQuoteView && hasThreadLoads ? (
                <MessageExistingLoads
                  threadLoadCount={threadLoadCount}
                  threadFreightTrackingIDs={threadFreightTrackingIDs ?? []}
                  onOpenLoadsView={openLoadsView}
                />
              ) : null
            }
            threadCheck={
              visibleThreadChecks.length > 0 ? (
                <>
                  {visibleThreadChecks.map((check) => (
                    <KitThreadCheckMessage key={check.kind} check={check} />
                  ))}
                  <AskKitThreadCheck checks={visibleThreadChecks} />
                </>
              ) : null
            }
          />

          {isQuoteView && cloneRecommendationsError && (
            <div className='rounded-md bg-error-50 p-2.5' role='alert'>
              <Typography variant='body-xs' className='text-error-700'>
                Could not refresh load clone recommendations. Showing the last
                available results.
              </Typography>
              <button
                type='button'
                className='mt-1 text-xs font-medium text-brand-main hover:underline'
                onClick={() => onCloneRecommendationsChanged?.()}
              >
                Try again
              </button>
            </div>
          )}

          {isQuoteView && hasCompletedSuggestions && (
            <CompletedSuggestionsToggle
              showCompleted={showCompletedSuggestions}
              onToggle={() => {
                const nextShowsCompleted = !showCompletedSuggestions;
                captureKitHandledQuoteViewToggled({
                  capture: posthogCapture,
                  view: nextShowsCompleted
                    ? KitHandledQuoteView.Previous
                    : KitHandledQuoteView.Pending,
                });
                setShowCompletedSuggestions(nextShowsCompleted);
              }}
              {...getCompletedToggleLabels(
                handledSuggestionCount,
                builtLoadCount,
                pendingSuggestionCount,
                pendingByPipeline[SuggestionPipelines.QuickQuote] ?? 0
              )}
              statuses={completedSuggestionStatuses}
            />
          )}

          {!hideSuggestionList &&
            (!isQuoteView ||
              !showCompletedSuggestions ||
              !hasCompletedSuggestions) && (
              <SuggestionList
                additionalLoadBuildingSuggestions={{
                  count: visibleCloneRecommendations.length,
                  pendingCount: pendingCloneCount,
                  hasSelection: selectedCloneRecommendationId != null,
                  rows: visibleCloneRecommendations.map(
                    (recommendation, index) => {
                      const status = cloneJobStatuses[recommendation.id];
                      const isBlocked =
                        blockedCloneRecommendationIds.includes(
                          recommendation.id
                        ) ||
                        mutatingCloneRecommendationIds.includes(
                          recommendation.id
                        );
                      const descriptor = recommendation.source
                        ? `Clone load ${recommendation.source.identifier}`
                        : 'Clone a previous load · Choose source';
                      const statusLabel = (() => {
                        if (status === CloneJobStatus.Failed)
                          return ' · Failed';
                        if (status === CloneJobStatus.Partial)
                          return ' · Needs review';
                        if (
                          recommendation.disposition ===
                            LoadCloneRecommendationDisposition.Started &&
                          status !== CloneJobStatus.Succeeded
                        )
                          return ' · View progress';
                        return '';
                      })();
                      return (
                        <Suggestion
                          key={`clone-${recommendation.id}`}
                          ordinal={`${index + 1}.`}
                          descriptor={descriptor + statusLabel}
                          fullTitle={descriptor + statusLabel}
                          disabled={mutatingCloneRecommendationIds.includes(
                            recommendation.id
                          )}
                          completed={status === CloneJobStatus.Succeeded}
                          dismissible={
                            recommendation.disposition ===
                              LoadCloneRecommendationDisposition.Pending &&
                            !isBlocked
                          }
                          isSelected={
                            selectedCloneRecommendationId === recommendation.id
                          }
                          isPreviewOpen={false}
                          onPreviewOpenChange={() => {}}
                          onRowPointerEnter={() => {}}
                          onApply={() => {
                            setExpandedPipeline(
                              SuggestionPipelines.LoadBuilding
                            );
                            if (
                              blockedCloneRecommendationIds.includes(
                                recommendation.id
                              )
                            ) {
                              void onCloneRecommendationConflict?.(
                                recommendation
                              );
                            }
                            onSelectCloneRecommendation?.(recommendation);
                          }}
                          onDismiss={() =>
                            dismissCloneRecommendation(recommendation)
                          }
                        />
                      );
                    }
                  ),
                }}
                pipelinesToShow={pipelinesToShow}
                groupedSuggestions={groupedSuggestions}
                clickedSuggestion={clickedSuggestion}
                expandedPipeline={expandedPipeline}
                setExpandedPipeline={setExpandedPipeline}
                visibleCountByPipeline={visibleCountByPipeline}
                setVisibleCountByPipeline={setVisibleCountByPipeline}
                isBatchQuoteEnabled={isBatchQuoteEnabled}
                batchSelectedSuggestionIds={batchSelectedSuggestionIds}
                handleApplySuggestion={handleApplySuggestion}
                handleApplyBatch={handleApplyBatch}
                handleDismissSuggestion={handleDismissSuggestion}
                appointmentTimezones={appointmentTimezones}
                tmsName={tmsName}
              />
            )}

          {handledQuoteRequests &&
            (!isQuoteView || showCompletedSuggestions) && (
              <HandledQuoteGroups
                groups={handledQuoteRequests.handled}
                selectedIds={selectedHandledQuoteIds}
                selectionEnabled={isQuoteRequestResetEnabled}
                onToggleLane={toggleHandledQuoteSelection}
                onSelectLanes={selectHandledQuoteLanes}
                onResetSelected={resetSelectedHandledQuotes}
              />
            )}

          {isQuoteView && showCompletedSuggestions && onOpenBuiltLoad && (
            <BuiltLoadsGroup
              key={threadId}
              suggestions={builtLoadBuildingSuggestions}
              onOpenLoad={onOpenBuiltLoad}
            />
          )}
        </Flex>
      </div>
    </TooltipProvider>
  );
}

const NO_THREAD_CHECKS: KitThreadCheckController[] = [];

function getHandledStatusIndicatorClass(state: HandledGroupState): string {
  switch (state) {
    case QuoteHandledState.Replied:
      return 'text-success-700';
    case QuoteHandledState.Quoted:
      return 'text-warning-900';
    case QuoteHandledState.Skipped:
      return 'text-neutral-600';
  }
  return '';
}

type CompletedToggleLabels = {
  viewCompletedLabel: string;
  viewPendingLabel: string;
};

// Toggle copy for what's behind it: handled quotes, built loads, or both. The pending side names quotes or
// loads only when every pending suggestion is one, since a thread can hold both.
function getCompletedToggleLabels(
  handledQuoteCount: number,
  builtLoadCount: number,
  pendingCount: number,
  pendingQuoteCount: number
): CompletedToggleLabels {
  if (builtLoadCount === 0) {
    return {
      viewCompletedLabel: `View handled quotes (${handledQuoteCount})`,
      viewPendingLabel:
        pendingQuoteCount === pendingCount
          ? `View pending quotes (${pendingCount})`
          : `View pending (${pendingCount})`,
    };
  }
  if (handledQuoteCount === 0) {
    return {
      viewCompletedLabel: `View built loads (${builtLoadCount})`,
      viewPendingLabel:
        pendingQuoteCount === 0
          ? `View pending loads (${pendingCount})`
          : `View pending (${pendingCount})`,
    };
  }
  return {
    viewCompletedLabel: `View handled quotes and built loads (${
      handledQuoteCount + builtLoadCount
    })`,
    viewPendingLabel: `View pending (${pendingCount})`,
  };
}

// Replied / quoted / skipped Quick Quote lanes leave the Kit list for HandledQuoteGroups.
// Other pipelines keep their Done pills.
function isHandledQuoteRequest(suggestion: GenericSuggestion): boolean {
  return (
    suggestion.pipeline === SuggestionPipelines.QuickQuote &&
    !isQuoteRequestActionable(suggestion)
  );
}

// Built load building cards leave the Kit list for BuiltLoadsGroup, except the card the rep has
// open when the refetch lands. Moving it would swap the form they're working in for another card.
function shouldMoveToBuiltGroup(
  suggestion: GenericSuggestion,
  clickedSuggestionId: Undef<number>
): boolean {
  return (
    isBuiltLoadBuildingSuggestion(suggestion) &&
    suggestion.id !== clickedSuggestionId
  );
}

// With nothing left to suggest, say so. When the thread has quote requests or load building
// cards, say every one is handled or built instead.
function getQuoteViewEmptyStateType(
  handledQuoteRequests: Maybe<PartitionedQuoteRequests>,
  loadBuildingSuggestions: Maybe<PartitionedLoadBuildingSuggestions>
): KitEmptyStateType {
  const hasActiveQuoteRequests = (handledQuoteRequests?.active.length ?? 0) > 0;
  const hasHandledQuoteRequests = HANDLED_GROUP_STATES.some(
    (state) => (handledQuoteRequests?.handled[state].length ?? 0) > 0
  );
  if (hasHandledQuoteRequests && !hasActiveQuoteRequests) {
    return KitEmptyStateType.AllQuotesHandled;
  }

  const hasOpenLoads = (loadBuildingSuggestions?.open.length ?? 0) > 0;
  const hasBuiltLoads = (loadBuildingSuggestions?.built.length ?? 0) > 0;
  if (hasBuiltLoads && !hasOpenLoads && !hasActiveQuoteRequests) {
    return KitEmptyStateType.AllLoadsBuilt;
  }

  return KitEmptyStateType.NoSuggestionsForQuoteView;
}
